/**
 * Choose languages from all ISO 639-1 languages (#1442): the chosen ones as
 * chips, the common ones as one-tap suggestions, and the rest by typing a name
 * in the reader's language, the language's own name, or its code ("viro",
 * "eesti", "et"). An ARIA combobox: arrows move, Enter picks, Escape closes.
 */
import React, { useId, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { X, Plus } from 'lucide-react';
import { REPORT_LANGUAGE_CODES, COMMON_REPORT_LANGUAGES, languageName } from '@kuutio/shared/reportLanguages.js';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const MAX_MATCHES = 8;

const Wrap = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$chosen ? 'rgba(74, 158, 255, 0.6)' : '#374151')};
  background: ${(p) => (p.$chosen ? 'rgba(74, 158, 255, 0.18)' : 'transparent')};
  color: ${(p) => (p.$chosen ? '#dbeafe' : '#cbd5e1')};
  &:hover { border-color: #4a9eff; color: #fff; }
`;

const Hint = styled.span`
  font-size: 12px;
  color: #9ca3af;
`;

const Input = styled.input`
  width: 100%;
  min-height: 40px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #374151;
  background: #111827;
  color: #e5e7eb;
  font-size: 14px;
  &:focus { outline: none; border-color: #4a9eff; }
`;

const List = styled.ul`
  position: absolute;
  z-index: 10;
  top: 100%;
  left: 0;
  right: 0;
  margin: 4px 0 0;
  padding: 4px;
  list-style: none;
  border-radius: 8px;
  border: 1px solid #374151;
  background: #111827;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
`;

const Option = styled.li`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  color: #e5e7eb;
  background: ${(p) => (p.$active ? 'rgba(74, 158, 255, 0.2)' : 'transparent')};
`;

const Native = styled.span`
  color: #9ca3af;
`;

/**
 * @param {string[]} value      chosen codes
 * @param {(codes: string[]) => void} onChange
 * @param {string[]} [exclude]  codes that cannot be chosen (the report's own, those already stored)
 * @param {string[]} [pinned]   one-tap suggestions
 */
export default function LanguagePicker({ value = [], onChange, exclude = [], pinned = COMMON_REPORT_LANGUAGES, disabled = false }) {
  const { t, language } = useTranslation();
  const listId = useId();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const all = useMemo(() => REPORT_LANGUAGE_CODES.map((code) => ({
    code,
    name: languageName(code, language),
    native: languageName(code, code),
  })), [language]);
  const byCode = useMemo(() => new Map(all.map((l) => [l.code, l])), [all]);

  const unavailable = new Set([...exclude, ...value]);
  const q = query.trim().toLowerCase();
  const matches = q
    ? all
      .filter((l) => !unavailable.has(l.code))
      .map((l) => {
        const names = [l.name.toLowerCase(), l.native.toLowerCase()];
        const rank = l.code === q ? 0 : names.some((n) => n.startsWith(q)) ? 1 : names.some((n) => n.includes(q)) ? 2 : -1;
        return { ...l, rank };
      })
      .filter((l) => l.rank >= 0)
      .sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name, language))
      .slice(0, MAX_MATCHES)
    : [];
  const suggestions = pinned.filter((c) => !unavailable.has(c));

  const add = (code) => {
    onChange([...value, code]);
    setQuery('');
    setActive(0);
  };
  const remove = (code) => onChange(value.filter((c) => c !== code));

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((i) => Math.min(i + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && matches[active]) { e.preventDefault(); add(matches[active].code); }
    else if (e.key === 'Escape') setOpen(false);
  };

  const showList = open && matches.length > 0;

  return (
    <Wrap>
      {value.length > 0 && (
        <Row>
          {value.map((code) => (
            <Chip key={code} type="button" $chosen disabled={disabled} onClick={() => remove(code)}
              aria-label={t('languagePicker.remove', { lang: byCode.get(code)?.name || code })}>
              {byCode.get(code)?.name || code} <X size={12} />
            </Chip>
          ))}
        </Row>
      )}
      {suggestions.length > 0 && (
        <Row>
          <Hint>{t('languagePicker.common')}</Hint>
          {suggestions.map((code) => (
            <Chip key={code} type="button" disabled={disabled} onClick={() => add(code)}>
              <Plus size={12} /> {byCode.get(code)?.name || code}
            </Chip>
          ))}
        </Row>
      )}
      <Input
        type="text"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={showList ? `${listId}-${matches[active]?.code}` : undefined}
        aria-label={t('languagePicker.search')}
        placeholder={t('languagePicker.search')}
        value={query}
        disabled={disabled}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(0); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
      />
      {open && q && matches.length === 0 && <Hint>{t('languagePicker.noMatch')}</Hint>}
      {showList && (
        <List id={listId} role="listbox">
          {matches.map((l, i) => (
            <Option
              key={l.code}
              id={`${listId}-${l.code}`}
              role="option"
              aria-selected={i === active}
              $active={i === active}
              // mousedown, not click: the input's blur would close the list first
              onMouseDown={(e) => { e.preventDefault(); add(l.code); }}
              onMouseEnter={() => setActive(i)}
            >
              <span>{l.name}</span>
              <Native>{l.native !== l.name ? `${l.native} · ` : ''}{l.code}</Native>
            </Option>
          ))}
        </List>
      )}
    </Wrap>
  );
}
